import { useState } from "react";
import { taskService } from "@miplatform/api";
import { DOCUMENT_TYPES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  createUUID,
  externalReferenceId,
  getRootUrl,
  getUniqueArrayByField,
} from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";
import { getAttachmentsFromDocuments } from "./util/emailFormUtil";
import { EMAIL_TEMPLATE_TYPE_TO_DRAWER_CONFIG } from "./emailFormConstants";

export const useEmailFormOrderViewModel = ({
  account,
  attempt,
  step,
  entity,
  templateType,
  files,
  documentCategory,
  getValues,
  onClose,
}) => {
  const { title, templateConfig, toast, getTemplate } =
    EMAIL_TEMPLATE_TYPE_TO_DRAWER_CONFIG[templateType];
  const { loading, uploadDocument } = useDocumentsViewModel();
  const [contacts, setContacts] = useState(null);
  const [isSent, setIsSent] = useState(false);
  const [order, setOrder] = useState(entity);
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const getEmailContacts = ({ entity, templateType }) =>
    stateManager.run(async () => {
      const response = await taskService.order.getEmailTemplateContacts({
        orderId: entity.id || entity.orderId,
        emailType: templateType,
      });

      if (response.status === HTTP_STATUSES.OK) {
        const { to, cc, relatedContacts } = response.data;
        setContacts({
          to: getUniqueArrayByField(to, "email"),
          cc: getUniqueArrayByField(cc, "email"),
          relatedContacts: getUniqueArrayByField(relatedContacts, "email"),
        });
      }
    });

  const getOrder = () =>
    stateManager.run(async () => {
      const { status, data } = await taskService.order.getById({
        orderId: entity.id || entity.orderId,
      });

      if (status === HTTP_STATUSES.OK) {
        setOrder(data);
      }
    });

  const sendEmail = async () => {
    const fields = getValues();
    const emailId = createUUID();
    let filePromises = [];

    if (files.length > 0) {
      const uploadPromises = files.map((file) =>
        uploadDocument({
          file,
          documentType: DOCUMENT_TYPES.UNKNOWN,
          category: documentCategory,
          externalReferenceId: externalReferenceId.getOrderEmailRefId({
            orderId: order.id || order.orderId,
            emailId,
          }),
        })
      );

      filePromises = await Promise.all(uploadPromises);
    }
    const attachments = getAttachmentsFromDocuments(filePromises, getRootUrl());
    const emailBody = {
      id: emailId,
      emailId,
      emailType: templateType,
      subject: fields.subject,
      orderId: order.id || order.orderId,
      to: fields.to.map(({ name, email }) => ({
        ...(name && { name }),
        email,
      })),
      cc: fields.cc.map(({ name, email }) => ({
        ...(name && { name }),
        email,
      })),
      attachments: filePromises,
      body: getTemplate({
        entity: order,
        fields,
        attachments,
        account,
        templateType,
        config: templateConfig,
      }),
    };

    if (step && !step.stepResult) {
      const response = await taskService.assignments.updateStepResult({
        attemptId: attempt?.id,
        assignmentId: attempt?.assignment?.id,
        stepId: step.id,
        data: {},
        toastMessageConfig: {},
      });

      if (response.status === HTTP_STATUSES.CREATED) {
        step.stepResult = response.data;
      } else {
        return;
      }
    }

    stateManager.run(async () => {
      const response = await (step
        ? taskService.assignments.submitStepResult
        : taskService.order.sendEmail)({
        attemptId: attempt?.id,
        assignmentId: attempt?.assignment?.id,
        stepId: step?.id,
        stepResultId: step?.stepResult?.id,
        data: step ? { EmailPayload: emailBody } : emailBody,
        orderId: order?.id,
        toastMessageConfig: {
          success: {
            text: `${title} Sent`,
            subtext: toast,
          },
          error: {
            text: `${title} Failed`,
            subtext: `Your ${title.toLowerCase()} was not sent, please try again`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        onClose();
      }
    });
  };

  const getEmailHistory = () =>
    stateManager.run(async () => {
      const response = await taskService.order.getEmailHistory({
        orderId: entity.id || entity.orderId,
      });

      if (response.status === HTTP_STATUSES.OK) {
        setIsSent(
          response.data.some((email) => email.emailType === templateType)
        );
      }
    });

  return {
    isLoading: loading || isLoading,
    isSent,
    contacts,
    entity: order,
    getEmailContacts,
    getEmailHistory,
    getEntity: getOrder,
    sendEmail,
  };
};
